
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, AlertIcon } from '../BorrowingsIcons';
import { toast } from "sonner";
import { applyPrepayment, applyMoratorium, createScheduleChangeEvent } from './utils/repaymentUtils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

interface PayoutAdjustmentProps {
  loan: any;
  onSave: (updatedRepayments: any[], event: any) => void;
  onCancel: () => void;
}

export const PayoutAdjustment: React.FC<PayoutAdjustmentProps> = ({ 
  loan, 
  onSave, 
  onCancel 
}) => {
  const [adjustmentType, setAdjustmentType] = useState<'prepayment' | 'moratorium'>('prepayment');
  
  // Prepayment states
  const [prepaymentAmount, setPrepaymentAmount] = useState<string>("");
  const [prepaymentType, setPrepaymentType] = useState<'reduce_emi' | 'reduce_tenure'>('reduce_emi');
  const [prepaymentDate, setPrepaymentDate] = useState<Date | undefined>(new Date());
  
  // Moratorium states
  const [moratoriumStartDate, setMoratoriumStartDate] = useState<Date | undefined>(new Date());
  const [moratoriumEndDate, setMoratoriumEndDate] = useState<Date | undefined>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 3); // Default 3 month moratorium
    return date;
  });
  const [deferInterest, setDeferInterest] = useState<boolean>(true);
  
  const handleSave = () => {
    try {
      let updatedRepayments;
      let event;
      
      if (adjustmentType === 'prepayment') {
        // Validate prepayment data
        const amount = parseFloat(prepaymentAmount);
        if (isNaN(amount) || amount <= 0) {
          toast.error("Please enter a valid prepayment amount");
          return;
        }
        
        if (!prepaymentDate) {
          toast.error("Please select a prepayment date");
          return;
        }
        
        // Apply prepayment to schedule
        updatedRepayments = applyPrepayment(
          loan, 
          amount, 
          prepaymentDate.toISOString().split('T')[0], 
          prepaymentType
        );
        
        // Create event
        event = createScheduleChangeEvent(loan, 'prepayment', {
          amount,
          date: prepaymentDate.toISOString().split('T')[0],
          type: prepaymentType === 'reduce_emi' ? 'Reduce EMI' : 'Reduce Tenure'
        });
      } else {
        // Validate moratorium data
        if (!moratoriumStartDate || !moratoriumEndDate) {
          toast.error("Please select valid moratorium dates");
          return;
        }
        
        if (moratoriumStartDate > moratoriumEndDate) {
          toast.error("End date cannot be before start date");
          return;
        }
        
        // Apply moratorium to schedule
        updatedRepayments = applyMoratorium(
          loan,
          moratoriumStartDate.toISOString().split('T')[0],
          moratoriumEndDate.toISOString().split('T')[0],
          deferInterest
        );
        
        // Create event
        event = createScheduleChangeEvent(loan, 'moratorium', {
          startDate: moratoriumStartDate.toISOString().split('T')[0],
          endDate: moratoriumEndDate.toISOString().split('T')[0],
          deferInterest
        });
      }
      
      onSave(updatedRepayments, event);
    } catch (error) {
      toast.error("Failed to update repayment schedule");
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertIcon className="h-5 w-5" />
          Adjust Repayment Schedule
        </CardTitle>
        <Tabs value={adjustmentType} onValueChange={(v) => setAdjustmentType(v as any)}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="prepayment">Prepayment</TabsTrigger>
            <TabsTrigger value="moratorium">Moratorium</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        {adjustmentType === 'prepayment' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Prepayment Amount</label>
              <Input 
                type="number" 
                value={prepaymentAmount}
                onChange={(e) => setPrepaymentAmount(e.target.value)}
                placeholder={`Amount in ${loan.currency}`}
                className="mt-1"
                min="1"
                required
              />
            </div>
            
            <div>
              <label className="text-sm font-medium block">Prepayment Date</label>
              <div className="mt-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {prepaymentDate ? format(prepaymentDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={prepaymentDate}
                      onSelect={setPrepaymentDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="text-sm font-medium block mb-2">Prepayment Application</label>
              <RadioGroup 
                defaultValue="reduce_emi"
                value={prepaymentType}
                onValueChange={(v) => setPrepaymentType(v as any)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="reduce_emi" id="reduce_emi" />
                  <Label htmlFor="reduce_emi">Reduce EMI (keep tenure same)</Label>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <RadioGroupItem value="reduce_tenure" id="reduce_tenure" />
                  <Label htmlFor="reduce_tenure">Reduce Tenure (keep EMI same)</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        )}
        
        {adjustmentType === 'moratorium' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block">Start Date</label>
                <div className="mt-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {moratoriumStartDate ? format(moratoriumStartDate, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={moratoriumStartDate}
                        onSelect={setMoratoriumStartDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block">End Date</label>
                <div className="mt-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {moratoriumEndDate ? format(moratoriumEndDate, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={moratoriumEndDate}
                        onSelect={setMoratoriumEndDate}
                        disabled={(date) => 
                          date < new Date() || 
                          (moratoriumStartDate && date < moratoriumStartDate)
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch 
                id="defer-interest"
                checked={deferInterest}
                onCheckedChange={setDeferInterest}
              />
              <Label htmlFor="defer-interest">Defer interest during moratorium</Label>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter className="border-t bg-muted/50 flex justify-end gap-2 pt-3">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>
          Apply Changes
        </Button>
      </CardFooter>
    </Card>
  );
};
